/**
 * AccountMarketingSection — Marketing Campaigns card + dialogs for AccountOverview.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Collapsible, CollapsibleContent } from '../../ui/collapsible';
import { Skeleton } from '../../ui/skeleton';
import { Megaphone, Mail, Phone } from 'lucide-react';
import { SectionHeader } from '../../common/OverviewHelpers';
import { MarketingDialog, MarketingDetailDialog } from '../ActivityDialogs';

const SectionSkeleton = ({ rows = 2 }) => (
  <div className="space-y-2">
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
  </div>
);

const getCampaignTypeIcon = (type) => {
  if (type === 'email') return <Mail className="w-3 h-3" />;
  if (type === 'telephone') return <Phone className="w-3 h-3" />;
  return <Megaphone className="w-3 h-3" />;
};

const AccountMarketingSection = ({
  account, canEditAccount,
  marketing = [], mktLoading,
  invalidateMarketing,
  contactEntries = [], usersMap = {},
}) => {
  const [open, setOpen] = useState(false);
  const [mktDialogOpen, setMktDialogOpen] = useState(false);
  const [mktDetailOpen, setMktDetailOpen] = useState(false);
  const [selectedMarketing, setSelectedMarketing] = useState(null);

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <Collapsible open={open} onOpenChange={setOpen}>
            <SectionHeader
              title="Marketing Campaigns"
              icon={Megaphone}
              isOpen={open}
              onToggle={() => setOpen(o => !o)}
              action={canEditAccount ? () => setMktDialogOpen(true) : undefined}
            />
            <CollapsibleContent className="mt-4">
              {mktLoading ? <SectionSkeleton rows={2} /> : marketing.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No active campaigns.</p>
              ) : (
                <div className="space-y-2">
                  {marketing.map(campaign => (
                    <div
                      key={campaign.id}
                      className="p-3 border rounded text-sm hover:bg-muted/50 cursor-pointer"
                      onClick={() => { setSelectedMarketing(campaign); setMktDetailOpen(true); }}
                      data-testid={`campaign-${campaign.id}`}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <Badge variant="secondary" className="text-xs flex items-center gap-1">
                          {getCampaignTypeIcon(campaign.campaign_type)}
                          {campaign.campaign_type?.replace('_', ' ')}
                        </Badge>
                        <Badge
                          variant={(campaign.is_active || campaign.status === 'active') ? 'default' : 'outline'}
                          className="text-[10px]"
                        >
                          {(campaign.is_active || campaign.status === 'active') ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="font-medium">{campaign.name}</p>
                      <p className="text-xs text-muted-foreground mt-1 capitalize">
                        Frequency: {campaign.frequency?.replace('_', ' ') || 'Not set'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      <MarketingDialog
        open={mktDialogOpen}
        onOpenChange={setMktDialogOpen}
        accountId={account?.id}
        contactEntries={contactEntries}
        onSuccess={invalidateMarketing}
      />

      <MarketingDetailDialog
        open={mktDetailOpen}
        onOpenChange={setMktDetailOpen}
        campaign={selectedMarketing}
        accountId={account?.id}
        contactEntries={contactEntries}
        canEditAccount={canEditAccount}
        usersMap={usersMap}
        onSuccess={invalidateMarketing}
      />
    </>
  );
};

export default AccountMarketingSection;
